import { yupResolver } from '@hookform/resolvers/yup';
import {
  useGetFormById,
  useGetFormFields,
  useGetProfile,
  useGetTicketTypesByEventId,
  useGetUserAdminProfile,
} from '@sunrise-ui/api-client';
import {
  DynamicForm,
  type DynamicFormHandle,
  type FormValues,
} from '@sunrise-ui/composites';
import {
  Button,
  ButtonVariants,
  Colors,
  Dropdown,
  InputError,
  Loader,
  PhoneInput,
  TextInput,
  TextReg,
  isNative,
  useAuthStore,
  useCheckoutModalStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Checkbox } from 'react-native-paper';
import { boolean, object, string, type InferType } from 'yup';
import { useOrderCalculations } from '../hooks/useOrderCalculations';
import { usePrepareOrder } from '../hooks/usePrepareOrder';
import { useScrollToError } from '../hooks/useScrollToError';
import { useCheckoutFormStore } from '../stores/useCheckoutFormStore';
import { usePaymentSheet } from '../stripe/usePaymentSheet';
import { OrderSummary } from './OrderSummary';
import { SmsConsentText } from './SmsConsentText';

const attendeeFormSchema = object().shape({
  firstName: string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters long'),
  lastName: string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters long'),
  email: string().required('Email is required').email('Invalid email address'),
  phoneNumber: string()
    .required('Phone number is required')
    .test(
      'is-valid-phone',
      'Please enter a 10 digit US phone number',
      (value) => {
        if (!value) return true;
        const digitsOnly = value.replace(/\D/g, '');
        return digitsOnly.length === 10;
      },
    ),
  ticketTypeId: string().required('Ticket type is required'),
  smsConsent: boolean().default(false),
});

type AttendeeFormValues = InferType<typeof attendeeFormSchema>;

const FIELD_ORDER: (keyof AttendeeFormValues)[] = [
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  'ticketTypeId',
];

// Helper function to normalize phone number from API format (+18382652756) to form format (8382652756)
const normalizePhoneNumber = (phoneNumber?: string): string => {
  if (!phoneNumber) return '';
  try {
    const parsed = parsePhoneNumberWithError(phoneNumber);
    return parsed.isValid() ? parsed.nationalNumber : '';
  } catch {
    return '';
  }
};

export function AttendeeDetailsStep() {
  const styles = useDynamicStyles(createStyles);
  const { currentUserId } = useAuthStore();
  const {
    setAttendee,
    setQuestionnaireData,
    setClientSecret,
    ticketQuantities,
    appliedPromo,
    attendee,
    questionnaireData,
  } = useCheckoutFormStore();
  const { eventId, setCurrentStep, setCheckoutId } = useCheckoutModalStore();
  const { prepareOrder, isPreparingOrder } = usePrepareOrder();
  const { launchPaymentSheet } = usePaymentSheet();
  const [submissionError, setSubmissionError] = useState<string>();
  const [questionnaireFormValues, setQuestionnaireFormValues] =
    useState<FormValues>(questionnaireData?.formValues ?? {});
  const questionnaireFormRef = useRef<DynamicFormHandle>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const { registerField, scrollToFirstError } =
    useScrollToError<AttendeeFormValues>(scrollViewRef, FIELD_ORDER);
  const questionnaireYPosition = useRef<number>(0);

  // Fetch user profile and email to prefill form
  const { data: profile, isLoading: isLoadingProfile } =
    useGetProfile(currentUserId);
  const { data: adminProfile, isLoading: isLoadingAdminProfile } =
    useGetUserAdminProfile();

  // Fetch ticket types to populate dropdown
  const { data: ticketTypes } = useGetTicketTypesByEventId({
    eventId,
  });

  // Get sorted ticket types that have quantities > 0
  const selectedTicketTypes = useMemo(() => {
    if (!ticketTypes) return [];
    return ticketTypes
      .filter((type) => type.id && (ticketQuantities.get(type.id) || 0) > 0)
      .sort((a, b) => a.price - b.price);
  }, [ticketTypes, ticketQuantities]);

  // Check if user is buying multiple different ticket types
  const hasMultipleTicketTypes = selectedTicketTypes.length > 1;

  // Get calculated total for button text (shares cache with OrderSummary)
  const {
    data: calculatedTotal,
    isError,
    isCalculating,
  } = useOrderCalculations({
    ticketQuantities,
    appliedPromo,
  });

  const hasInitialised = useRef(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm<AttendeeFormValues>({
    defaultValues: {
      firstName: attendee?.firstName || '',
      lastName: attendee?.lastName || '',
      email: attendee?.email || '',
      phoneNumber: attendee?.phoneNumber || '',
      ticketTypeId: attendee?.ticketTypeId || '',
    },
    resolver: yupResolver(attendeeFormSchema),
  });

  // Watch the current ticket type selection from the form
  const currentTicketTypeId = watch('ticketTypeId');

  // Get the selected form id
  const formId = useMemo(() => {
    if (!selectedTicketTypes || selectedTicketTypes.length === 0)
      return undefined;

    // Use current form value, fallback to attendee value or first selected type
    return selectedTicketTypes.find((type) => type.id === currentTicketTypeId)
      ?.form_id;
  }, [selectedTicketTypes, currentTicketTypeId]);

  // Fetch form fields for the selected ticket type
  const { data: formFields = [], isLoading: isLoadingFormFields } =
    useGetFormFields(formId);

  const { data: questionnaireForm } = useGetFormById(formId);

  // Prefill form with user data when available
  useEffect(() => {
    if (
      !hasInitialised.current &&
      !isLoadingProfile &&
      !isLoadingAdminProfile &&
      selectedTicketTypes.length > 0
    ) {
      hasInitialised.current = true;

      // Validate that attendee's ticketTypeId is still in the selected ticket types
      const isAttendeeTicketTypeValid =
        attendee?.ticketTypeId &&
        selectedTicketTypes.some((type) => type.id === attendee.ticketTypeId);

      reset({
        firstName: attendee?.firstName || profile?.first_name || '',
        lastName: attendee?.lastName || profile?.last_name || '',
        email: attendee?.email || adminProfile?.email || '',
        phoneNumber:
          attendee?.phoneNumber ||
          normalizePhoneNumber(adminProfile?.phone_number),
        ticketTypeId: isAttendeeTicketTypeValid
          ? attendee.ticketTypeId
          : selectedTicketTypes[0]?.id || '',
      });
    }
  }, [
    profile,
    adminProfile,
    selectedTicketTypes,
    attendee,
    reset,
    isLoadingProfile,
    isLoadingAdminProfile,
  ]);

  const onSubmit = async (data: AttendeeFormValues) => {
    if (!calculatedTotal) return;

    // Validate questionnaire form if it exists
    if (formId) {
      const isValid = questionnaireFormRef.current?.validate();
      if (!isValid) {
        // Scroll to questionnaire section to show errors (bit imprecise but good enough for now)
        scrollViewRef.current?.scrollTo({
          y: questionnaireYPosition.current + 50,
          animated: true,
        });
        return;
      }
      // Store questionnaire data if form exists (will be submitted after successful checkout)
      setQuestionnaireData({
        formId,
        formValues: questionnaireFormValues,
      });
    }

    // Clear any previous errors
    setSubmissionError(undefined);
    setAttendee(data);

    // Call prepareOrder for both free and paid tickets
    const { isSuccess, error, checkoutId, clientSecret, status } =
      await prepareOrder();

    if (!isSuccess) {
      setSubmissionError(error || 'Failed to initialize checkout');
      return;
    }

    // Branch 1: Free tickets - already completed, navigate to summary
    if (status === 'completed') {
      setCurrentStep('summary');
      return;
    }

    // Branch 2: Paid tickets - need clientSecret
    if (!clientSecret || !checkoutId) {
      setSubmissionError('Failed to initialize payment');
      return;
    }
    setClientSecret(clientSecret);
    setCheckoutId(checkoutId);

    // Branch 2a: Native - launch Payment Sheet and navigate to summary
    if (isNative) {
      const result = await launchPaymentSheet(clientSecret);
      if (result.isSuccess) {
        setCurrentStep('summary');
      } else {
        setCheckoutId(undefined);
        setClientSecret(undefined);
        if (!result.cancelled && result.error) {
          setSubmissionError(result.error);
        }
      }
      return;
    }

    // Branch 2b: Web - go to payment step
    setCurrentStep('payment');
  };

  const actionButtonText =
    (calculatedTotal?.total ?? 0) > 0
      ? 'Next'
      : (questionnaireForm?.submit_button_text ?? 'Register');

  return (
    <View style={styles.container}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardDismissMode="interactive"
        showsVerticalScrollIndicator={false}
      >
        <OrderSummary
          ticketQuantities={ticketQuantities}
          appliedPromo={appliedPromo}
          isExpandable={true}
        />

        <View style={styles.form}>
          <View
            style={styles.inputsRow}
            onLayout={(e) => registerField('firstName', e.nativeEvent.layout.y)}
          >
            <View style={styles.rowInput}>
              <Controller
                control={control}
                render={({ field }) => (
                  <TextInput label="First Name" {...field} dense={true} />
                )}
                name="firstName"
              />
              <InputError size="small" fieldError={errors.firstName} />
            </View>
            <View
              style={styles.rowInput}
              onLayout={(e) =>
                registerField('lastName', e.nativeEvent.layout.y)
              }
            >
              <Controller
                control={control}
                render={({ field }) => (
                  <TextInput label="Last Name" {...field} dense={true} />
                )}
                name="lastName"
              />
              <InputError size="small" fieldError={errors.lastName} />
            </View>
          </View>

          <View
            style={styles.inputsRow}
            onLayout={(e) => registerField('email', e.nativeEvent.layout.y)}
          >
            <View style={styles.rowInput}>
              <Controller
                control={control}
                render={({ field }) => (
                  <TextInput
                    label="Email"
                    {...field}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    dense={true}
                  />
                )}
                name="email"
              />
              <InputError size="small" fieldError={errors.email} />
            </View>

            <View
              style={styles.rowInput}
              onLayout={(e) =>
                registerField('phoneNumber', e.nativeEvent.layout.y)
              }
            >
              <Controller
                control={control}
                render={({ field }) => (
                  <PhoneInput label="Phone Number" {...field} dense={true} />
                )}
                name="phoneNumber"
              />
              <InputError size="small" fieldError={errors.phoneNumber} />
            </View>
          </View>

          <View style={styles.smsConsentContainer}>
            <TextReg>Want reminders and 24/7 support?</TextReg>
            <Controller
              control={control}
              render={({ field: { value, onChange } }) => (
                <TouchableOpacity
                  style={styles.checkboxRow}
                  onPress={() => onChange(!value)}
                  activeOpacity={0.7}
                >
                  <Checkbox.Android status={value ? 'checked' : 'unchecked'} />
                  <SmsConsentText />
                </TouchableOpacity>
              )}
              name="smsConsent"
            />
          </View>

          {hasMultipleTicketTypes && (
            <View
              style={styles.fullWidthInput}
              onLayout={(e) =>
                registerField('ticketTypeId', e.nativeEvent.layout.y)
              }
            >
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    label="Which ticket is for you?"
                    options={selectedTicketTypes.map((type) => ({
                      label: type.name,
                      value: type.id,
                    }))}
                    value={value}
                    onChange={(value) => {
                      setQuestionnaireFormValues({});
                      onChange(value);
                    }}
                    notClearable
                    variant="slim"
                  />
                )}
                name="ticketTypeId"
              />
              <InputError size="small" fieldError={errors.ticketTypeId} />
            </View>
          )}

          {formId && (
            <View
              onLayout={(event) => {
                questionnaireYPosition.current = event.nativeEvent.layout.y;
              }}
            >
              {isLoadingFormFields ? (
                <View style={styles.loadingContainer}>
                  <Loader />
                </View>
              ) : formFields.length > 0 ? (
                <DynamicForm
                  ref={questionnaireFormRef}
                  fields={formFields}
                  initialValues={questionnaireFormValues}
                  onChange={setQuestionnaireFormValues}
                  showSubmitButton={false}
                  disabled={false}
                />
              ) : null}
            </View>
          )}
        </View>
      </ScrollView>

      <View style={styles.footer}>
        {submissionError && (
          <TextReg style={styles.errorText}>{submissionError}</TextReg>
        )}
        <View style={styles.buttonRow}>
          <Button
            variant={ButtonVariants.WHITE}
            size="medium"
            onPress={() => {
              setCheckoutId(undefined);
              setClientSecret(undefined);
              setCurrentStep('ticketSelection');
            }}
            style={styles.button}
            disabled={isPreparingOrder}
          >
            Back
          </Button>
          <Button
            variant={ButtonVariants.PURPLE}
            size="medium"
            onPress={handleSubmit(onSubmit, (formErrors) => {
              questionnaireFormRef.current?.validate();
              scrollToFirstError(formErrors);
            })}
            disabled={
              isError ||
              isCalculating ||
              isPreparingOrder ||
              isLoadingFormFields
            }
            loading={isCalculating || isPreparingOrder || isLoadingFormFields}
            hideChildrenOnLoading={true}
            style={styles.button}
          >
            {actionButtonText}
          </Button>
        </View>
      </View>
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      padding: isMobile ? 12 : 20,
      alignItems: 'center',
      gap: 24,
    },
    form: {
      gap: 16,
      width: '100%',
      maxWidth: isMobile ? 500 : 700,
    },
    inputsRow: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 16,
      justifyContent: 'space-between',
      width: '100%',
    },
    rowInput: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: '50%',
    },
    fullWidthInput: {
      width: '100%',
      gap: 12,
    },
    smsConsentContainer: {
      width: '100%',
      gap: 8,
    },
    checkboxRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
    },
    footer: {
      padding: isMobile ? 12 : 16,
      gap: 12,
      alignItems: 'center',
    },
    buttonRow: {
      gap: 16,
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
    },
    button: {
      minWidth: isMobile ? 120 : 150,
    },
    errorText: {
      color: Colors.error,
      textAlign: 'center',
      paddingHorizontal: 16,
    },
    questionnaireContainer: {
      width: '100%',
      gap: 16,
    },
    loadingContainer: {
      alignItems: 'center',
      padding: 24,
      gap: 12,
    },
    loadingText: {
      fontSize: 14,
      color: Colors.placeholderText,
    },
  });
